/* aviaf — main app + router */
const { useState: useApp, useEffect: useAppE } = React;

// Hard-fail at module load if data-live.jsx didn't run first.  app.jsx
// invokes window.useLiveData unconditionally inside App() — calling a
// missing hook would crash deeper in React with a confusing error,
// while flipping it conditional would violate Rules of Hooks.  Catch
// the load-order regression up front.
if (typeof window.useLiveData !== "function") {
  throw new Error(
    "aviaf app.jsx loaded before data-live.jsx — check <script> order in index.html"
  );
}

function useHash() {
  const [h, setH] = useApp(location.hash || "#/overview");
  useAppE(() => {
    const on = () => setH(location.hash || "#/overview");
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return h;
}

/* Mobile detection — listens to viewport changes so a desktop window
 * resize crosses the breakpoint without a page reload.  Threshold
 * matches the iPhone 14 Pro landscape width (852px) ÷ 2 with a small
 * cushion: anything ≤ 760px CSS-px goes to the mobile UX, the rest
 * gets the desktop console. */
function useIsMobile() {
  const mql = typeof window.matchMedia === "function"
    ? window.matchMedia("(max-width: 760px)")
    : null;
  const [isM, setIsM] = useApp(!!(mql && mql.matches));
  useAppE(() => {
    if (!mql) return;
    const on = (e) => setIsM(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", on);
    else mql.addListener(on);  // Safari < 14
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", on);
      else mql.removeListener(on);
    };
  }, []);
  return isM;
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "paper",
  "density": "normal",
  "accentHue": 215,
  "showGrid": true,
  "tickRate": 1.0
}/*EDITMODE-END*/;

function TweaksUI({ tweaks, setTweak }) {
  return (
    <window.TweaksPanel title="Tweaks">
      <window.TweakSection title="Theme">
        <window.TweakRadio label="Surface" value={tweaks.theme}
          options={[{value:"paper",label:"Paper"},{value:"dark",label:"Dark"},{value:"terminal",label:"Terminal"}]}
          onChange={v=>setTweak("theme",v)} />
        <window.TweakRadio label="Density" value={tweaks.density}
          options={[{value:"compact",label:"Compact"},{value:"normal",label:"Normal"},{value:"comfy",label:"Comfy"}]}
          onChange={v=>setTweak("density",v)} />
        <window.TweakSlider label="Accent hue" min={0} max={360} step={1}
          value={tweaks.accentHue} onChange={v=>setTweak("accentHue",v)} />
        <window.TweakToggle label="Plot grid lines" value={tweaks.showGrid} onChange={v=>setTweak("showGrid",v)} />
      </window.TweakSection>
    </window.TweaksPanel>
  );
}

const NAV = [
  { k: "overview",   l: "Overview",   href: "#/overview" },
  { k: "instances",  l: "Instances",  href: "#/instances" },
  { k: "market",     l: "Market",     href: "#/market" },
  { k: "collectors", l: "Collectors", href: "#/collectors" },
  { k: "accounts",   l: "Accounts",   href: "#/accounts" },
  { k: "strategies", l: "Strategies", href: "#/strategies" },
  { k: "configs",    l: "Configs",    href: "#/configs" },
  { k: "research",   l: "Research",   href: "#/research" },
  { k: "alerts",     l: "Alerts",     href: "#/alerts" },
  { k: "ops",        l: "Ops",        href: "#/ops" },
];

/* Wall clocks tick on their own 1Hz timer so the header time advances
 * even when no live-data frames are arriving (market closed / overnight).
 * Isolated in its own component so this 1Hz repaint touches only the two
 * <span>s below — the App shell repaints only on live-data bumps. */
function TopClock() {
  const [now, setNow] = useApp(() => new Date());
  useAppE(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const utc = now.toLocaleTimeString("en-US", { hour12: false, timeZone: "UTC" });
  const ct = now.toLocaleTimeString("en-US", { hour12: false, timeZone: "America/Chicago" });
  return (
    <>
      <span className="tnum">{ct} CT</span>
      <span className="tnum muted">{utc} UTC</span>
    </>
  );
}

/* Live WebSocket latency netgraph: client-measured RTT (browser → hub →
 * browser) and server-measured RTT (hub → browser → hub), both updated in
 * real time off the WS ping/pong frames (see data-live.jsx applyFrame).
 * Reads window.__LIVE.ping{Client,Server}; App re-renders on the bump those
 * frames trigger, so the numbers tick live without a timer here. */
function PingMeter() {
  const L = window.__LIVE || {};
  // Display the RFC 6298 smoothed RTT (srtt), not the jittery raw sample —
  // this is what network monitors show.  Fall back to the raw latest only
  // before the estimator has its first sample.
  const pc = L.pingClientSrtt ?? L.pingClient;
  const ps = L.pingServerSrtt ?? L.pingServer;
  if (pc == null && ps == null) return null;
  const cls = (v) => (v == null ? "muted" : v < 80 ? "pos" : v < 200 ? "" : "neg");
  const fmt = (v) => (v == null ? "—" : v + "ms");
  // Fixed-width, right-aligned number boxes (header is a mono font → 1ch = one
  // char) so the bar doesn't shift when a value flips between 2 and 3 digits.
  // 5ch fits up to "999ms"; values never reuse a wider box in normal range.
  const num = { display: "inline-block", minWidth: "5ch", textAlign: "right" };
  const stat = (label, srtt, jit, p95) =>
    `${label}: ${srtt == null ? "—" : srtt + "ms"} avg` +
    (jit != null ? ` · ±${jit}ms jitter` : "") +
    (p95 != null ? ` · ${p95}ms p95` : "");
  const title =
    "WebSocket round-trip latency (RFC 6298 smoothed)\n" +
    stat("client", L.pingClientSrtt, L.pingClientJit, L.pingClientP95) + "\n" +
    stat("server", L.pingServerSrtt, L.pingServerJit, L.pingServerP95) +
    "\nboth measure the browser↔hub link; server = hub-clocked (also reflects this tab's reply speed)";
  return (
    <span className="ping-meter tnum" title={title}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
      <span className="muted" aria-hidden="true">⇅</span>
      <span className={"ping-val " + cls(pc)} style={num}>{fmt(pc)}</span>
      <span className="muted">· srv</span>
      <span className={"ping-val " + cls(ps)} style={num}>{fmt(ps)}</span>
    </span>
  );
}

function App() {
  const [tweaks, setTweak] = window.useTweaks(TWEAK_DEFAULTS);
  // Subscribe the shell to live-data bumps (useSyncExternalStore).  App
  // now repaints on actual data change — never on an idle interval — so
  // the topbar heartbeat + nav badge stay current with zero polling.
  // The wall clock advances on its own 1Hz timer inside <TopClock/> so
  // the header time still ticks when no frames are arriving.
  const rev = window.useLive();
  // session shape mirrors /api/auth/me: {auth_enabled, subject?}.
  //   null  = boot in progress, not yet known (render nothing — avoids
  //           flashing the login screen for half a second on hot reload)
  //   false = not authed; render LoginScreen
  //   {…}   = authed; render dashboard
  // We deliberately do NOT trust localStorage here — the design drop
  // tried to gate the UI on a localStorage flag, which let fake
  // credentials "log in" while the API rejected every call.  The hub
  // cookie is the only source of truth.
  const [session, setSession] = useApp(null);
  const hash = useHash();
  const isMobile = useIsMobile();

  // Kick off WS bridge + bootstrap fetch.  Re-runs when session
  // transitions (logged-out → in or in → out).
  //
  // CALL UNCONDITIONALLY — the previous `if (window.useLiveData)` guard
  // looked safe but is a Rules-of-Hooks violation: `useLiveData`
  // internally invokes React hooks (useEffect, useState).  If
  // data-live.jsx ever fails to load, the conditional would shift the
  // hook order on subsequent renders and crash with "Rendered fewer
  // hooks than expected" instead of degrading cleanly.  data-live.jsx
  // is included before app.jsx in index.html; the assertion at the
  // top of this file enforces that ordering at boot.
  window.useLiveData(session);

  // Code-split: the mobile UX (mobile.jsx, ~115 KB raw) is fetched only
  // when the viewport actually crosses into the mobile breakpoint.  Desktop
  // operators — the common case for an ops console — never download it.
  // esbuild sees this dynamic import() and emits mobile as its own hashed
  // chunk; the module's `Object.assign(window,{MobileApp})` side-effect
  // attaches the global the render branch below waits for.  Called
  // unconditionally (Rules of Hooks); the effect body is the guard.
  const [, bumpMobile] = useApp(0);
  useAppE(() => {
    if (isMobile && !window.MobileApp) {
      import("./mobile.jsx").then(() => bumpMobile((n) => n + 1)).catch(() => {});
    }
  }, [isMobile]);

  // Boot: ask /api/auth/me whether the cookie is still valid (or
  // whether auth is disabled in dev).  401 → fall through to LoginScreen.
  useAppE(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/auth/me", { credentials: "same-origin" });
        if (!alive) return;
        if (r.ok) setSession(await r.json());
        else setSession(false);
      } catch (_) {
        if (alive) setSession(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useAppE(() => {
    document.documentElement.dataset.theme = tweaks.theme;
    document.documentElement.dataset.density = tweaks.density;
    document.documentElement.style.setProperty("--accent", `oklch(0.62 0.11 ${tweaks.accentHue})`);
    document.documentElement.style.setProperty("--accent-soft", `oklch(0.62 0.11 ${tweaks.accentHue} / 0.12)`);
  }, [tweaks.theme, tweaks.density, tweaks.accentHue]);

  // Listen for data-live.jsx telling us /api/auth/me 401'd mid-session
  // (cookie expired between login and the next WS-triggered refresh).
  // Flipping session=false unmounts the dashboard and renders
  // <LoginScreen /> instead of leaving the operator looking at empty
  // KPIs because state got wiped by an auth-fail in the bootstrap.
  useAppE(() => {
    const onAuthLost = () => setSession(false);
    window.addEventListener("aviaf:auth-lost", onAuthLost);
    return () => window.removeEventListener("aviaf:auth-lost", onAuthLost);
  }, []);

  // Expose the resolved session to the CR/Alerts/Ops data layer so its
  // journalled mutations can label the optimistic actor (the server still
  // derives the real audit actor from the JWT — this is display-only).
  useAppE(() => { window.__OPS_SESSION = session || null; }, [session]);

  // Boot still in flight — render nothing rather than flashing the login.
  if (session === null) return null;

  if (session === false) {
    return <window.LoginScreen tweaks={tweaks} cycleTheme={() => {
      const order = ["paper","dark","terminal"]; const i = order.indexOf(tweaks.theme);
      setTweak("theme", order[(i+1)%order.length]);
    }} onAuth={async () => {
      // Re-pull /api/auth/me to populate subject after login completes
      // (login.jsx calls real /api/auth/login + /api/auth/verify and
      //  passes the response body, but we re-fetch to get the canonical
      //  /me shape with subject + auth_enabled).
      try {
        const r = await fetch("/api/auth/me", { credentials: "same-origin" });
        setSession(r.ok ? await r.json() : { auth_enabled: true, subject: "operator" });
      } catch (_) {
        setSession({ auth_enabled: true, subject: "operator" });
      }
    }} />;
  }

  const logout = async () => {
    try { await fetch("/api/auth/logout", { method: "POST", credentials: "same-origin" }); } catch (_) {}
    setSession(false);
  };

  // Mobile UX takes over the entire viewport when the breakpoint is
  // active.  All hooks above (session bootstrap, theme application,
  // useLiveData, tick interval) have run, so the rules-of-hooks order
  // is preserved across desktop/mobile transitions.  MobileApp reads
  // window.* live data populated by useLiveData(session) above.
  if (isMobile) {
    if (window.MobileApp) {
      return (
        <window.MobileApp
          session={session}
          onLogout={logout}
          tweaks={tweaks}
          setTweak={setTweak}
        />
      );
    }
    // Mobile chunk still in flight — a brief splash rather than flashing
    // the full desktop layout on a phone.  The effect above forces a
    // re-render the moment window.MobileApp lands.
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", color: "var(--muted)" }}>
        loading…
      </div>
    );
  }

  // route
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  const top = parts[0] || "overview";
  const sub = parts[1];

  let bodyRaw;
  if (top === "overview") bodyRaw = <window.PageOverview tweaks={tweaks} />;
  else if (top === "instances") bodyRaw = sub ? <window.PageInstanceDetail id={sub} /> : <window.PageInstances />;
  else if (top === "accounts") bodyRaw = sub ? <window.PageAccountDetail id={sub} tweaks={tweaks} /> : <window.PageAccounts />;
  else if (top === "strategies") {
    if (sub) {
      const classes = window.STRATEGY_CLASSES || [];
      const defs = window.STRATEGY_DEFS || [];
      const isClass = classes.some(c => c.id === sub);
      if (isClass) {
        bodyRaw = <window.PageStrategyClass klass={sub} tweaks={tweaks} />;
      } else {
        const def = defs.find(d => d.id === sub);
        if (def) {
          // legacy def URL → render class page, highlight the def
          bodyRaw = <window.PageStrategyClass klass={def.klass} highlightDefId={sub} tweaks={tweaks} />;
        } else {
          bodyRaw = <window.PageStrategyDetail id={sub} tweaks={tweaks} />;
        }
      }
    } else {
      bodyRaw = <window.PageStrategies />;
    }
  }
  else if (top === "research") bodyRaw = <window.PageResearch />;
  else if (top === "market") bodyRaw = sub ? <window.PageMarketDetail id={sub} /> : <window.PageMarket />;
  else if (top === "collectors") bodyRaw = sub ? <window.PageCollectorDetail id={sub} /> : <window.PageCollectors />;
  else if (top === "configs") bodyRaw = sub ? <window.PageConfigDetail id={sub} /> : <window.PageConfigs />;
  else if (top === "alerts") bodyRaw = <window.PageAlerts initialId={sub || null} />;
  else if (top === "ops") bodyRaw = <window.PageTechops />;
  else bodyRaw = <Stub title="Not found" sub={hash} />;
  // Page-level error boundary: one bad row in a downstream component
  // shouldn't blank the dashboard.  Boundary key on hash so route
  // changes reset the error state and re-attempt rendering.
  const body = window.ErrorBoundary
    ? <window.ErrorBoundary key={hash} label={`page · ${top}`}>{bodyRaw}</window.ErrorBoundary>
    : bodyRaw;

  return (
    <div className="app">
      <div className="topbar">
        <div className="brand">
          <span className="brand-mark" />
          aviaf <small>ops · v0.7.2</small>
        </div>
        <div className="topnav">
          {NAV.map(n => {
            const critN = n.k === "alerts" && window.openCritAlertCount ? window.openCritAlertCount() : 0;
            return (
              <a key={n.k} href={n.href} style={{textDecoration:"none"}}>
                <button data-active={top === n.k}>
                  {n.l}
                  {critN > 0 && <span className="nav-badge">{critN}</span>}
                </button>
              </a>
            );
          })}
        </div>
        <div className="topright">
          <span className="heartbeat">
            <span className={"dot " + (window.__LIVE?.wsStatus === "live" ? "" : "warn")} />
            {(() => {
              if (!window.__LIVE?.hasData) return "loading…";
              const accts = (window.ACCOUNTS || []).length;
              const stratSet = new Set((window.ACCOUNTS || []).flatMap(a => a.strategies || []));
              const live = (window.INSTANCES || []).filter(i => i.state === "running").length;
              const total = (window.INSTANCES || []).length;
              return `${live}/${total} agent · ${accts} acct · ${stratSet.size} strat`;
            })()}
          </span>
          <PingMeter />
          <TopClock />
          <button className="theme-toggle" title="Cycle theme" onClick={() => {
            const order = ["paper","dark","terminal"]; const i = order.indexOf(tweaks.theme);
            setTweak("theme", order[(i+1)%order.length]);
          }}>
            {tweaks.theme==="paper" ? "☼ paper" : tweaks.theme==="dark" ? "☾ dark" : "▮ term"}
          </button>
          <window.UserMenu session={session} onLogout={logout} />
        </div>
      </div>

      {/* CR/Alerts/Ops global elements — the open-crit banner (§3.3) shows on
          EVERY tab, plus the shared incident drawer.  Wrapped in .ops-console
          so the scoped design CSS applies; existing pages stay untouched.
          Mounting CritBanner here also subscribes the ops data layer, keeping
          the nav crit badge and banner live across all tabs. */}
      {(window.CritBanner || window.OpsGlobal) && (
        <div className="ops-console">
          {window.CritBanner && <window.CritBanner />}
          {window.OpsGlobal && <window.OpsGlobal />}
        </div>
      )}

      {body}

      {window.ToastHost && <window.ToastHost />}
      {window.ConfirmHost && <window.ConfirmHost />}

      <div className="footer">
        <div className="keys">
          <span><kbd>O</kbd>overview</span>
          <span><kbd>I</kbd>instances</span>
          <span><kbd>A</kbd>accounts</span>
          <span><kbd>S</kbd>strategies</span>
          <span><kbd>R</kbd>research</span>
          <span><kbd>/</kbd>search</span>
        </div>
        <div>aviaf@host01 · build 2026.05.05 · session 6h 12m · {rev} updates</div>
      </div>

      {window.ConnectionBanner && <window.ConnectionBanner />}

      <TweaksUI tweaks={tweaks} setTweak={setTweak} />
    </div>
  );
}

function Stub({ title, sub }) {
  return (
    <div style={{padding:"60px var(--pad)", color:"var(--muted)", fontFamily:"var(--mono)"}}>
      <div style={{fontSize:14,color:"var(--ink)"}}>{title}</div>
      <div style={{fontSize:12,marginTop:6}}>{sub}</div>
    </div>
  );
}



ReactDOM.createRoot(document.getElementById("root")).render(<App />);
